import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import type { IServices } from "../../CustomHooks/useMarketBuilderServices";
import type { FC } from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
    ImportIconButton,
    ModalFooter,
    ModalTitle,
    SectionBody,
    SectionTitle,
    UploadedFileBox,
    UploadedFileName,
    UploadedFileStats,
    CancelButton,
} from "./ImportDomainsModal.styles";
import { SWReactIcons } from "@similarweb/icons";
import { CSVFileDropZone } from "./CSVFileDropZone";
import { AssetsService } from "app/services/AssetsService";
import {
    getAllowedFileTypesString,
    importFileContent,
} from "app/pages/industry-analysis/market-builder/services/ImportDomainsHandler";
import type { IDomain } from "app/pages/industry-analysis/market-builder/MarketBuilder.types";
import { allTrackers } from "app/services/track/track";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { StyledCSVFileSuccessImage } from "./ImportDomains.styles";

const proModalStyles: IProModalCustomStyles = {
    content: {
        width: "420px",
        padding: "24px",
    },
    overlay: {
        backgroundColor: rgba(colorsPalettes.midnight[300], 0.7),
    },
};

const EVENT_CATEGORY = "Button";
const EVENT_ACTION = "click";

interface IImportCSVModalProps {
    isOpen?: boolean;
    onImportSuccess: (domains: IDomain[], uploadedFile: File) => void;
    onImportFailure?: (errorMsg: string, uploadedFiles: File[]) => void;
    onClose: VoidFunction;
    onDownloadSampleCsvFile?: VoidFunction;
    modalTitle: string;
    services: IServices;
    selectedDomains: IDomain[];
}

export const ImportDomainsModal: FC<IImportCSVModalProps> = (props) => {
    const {
        services,
        isOpen = false,
        onImportSuccess,
        onImportFailure,
        onClose,
        onDownloadSampleCsvFile,
        modalTitle,
        selectedDomains,
    } = props;

    const [uploadedFile, setUploadedFile] = useState<File>(null);
    const [uploadErrorMsg, setUploadErrorMsg] = useState<string>(null);
    const [importedDomains, setImportedDomains] = useState<IDomain[]>(null);
    const [isImporting, setIsImporting] = useState<boolean>(false);

    const reset = useCallback(() => {
        setUploadedFile(null);
        setUploadErrorMsg(null);
        setImportedDomains(null);
        setIsImporting(false);
    }, [setUploadedFile, setUploadErrorMsg, setImportedDomains, setIsImporting]);

    // reset state on open
    useEffect(() => {
        if (isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const handleError = useCallback(
        (error, uploadedFiles) => {
            setUploadErrorMsg(error);
            if (onImportFailure) {
                onImportFailure(error, uploadedFiles);
            }
        },
        [onImportFailure],
    );

    const handleFiles = useCallback(
        async (files: File[]) => {
            setUploadErrorMsg(null);
            const importResult = await importFileContent(files, services, selectedDomains);

            if (!importResult.isSuccess) {
                handleError(importResult.errorMessage, files);
                return;
            }

            // set uploaded file and its extracted strings
            setImportedDomains(importResult.domains);
            setUploadedFile(importResult.file);
        },
        [handleError, services],
    );

    const importDomains = useCallback(async () => {
        setIsImporting(true);
        await onImportSuccess(importedDomains, uploadedFile);
        setIsImporting(false);
        allTrackers.trackEvent(EVENT_CATEGORY, EVENT_ACTION, `By websites/importModal/${importedDomains.length}`);
        onClose();
    }, [importedDomains, onClose, onImportSuccess, uploadedFile]);

    const handleDownloadSampleCsvFile = () => {
        allTrackers.trackEvent(EVENT_CATEGORY, EVENT_ACTION, `By websites/importModal/downloadSampleCsv`);
        onDownloadSampleCsvFile && onDownloadSampleCsvFile();
    };
    const renderUploadFile = useCallback(() => {
        return (
            <>
                <CSVFileDropZone
                    handleFiles={handleFiles}
                    error={uploadErrorMsg}
                    acceptFile={getAllowedFileTypesString()}
                    services={services}
                />
                <SectionTitle>{services.translate("marketbuilder.importModal.section.optional")}</SectionTitle>
                <SectionBody>
                    <a
                        onClick={() => handleDownloadSampleCsvFile()}
                        href={AssetsService.assetUrl("/images/industries/files/bulk_upload_sample.csv")}
                    >
                        <SWReactIcons
                            iconName="download"
                            size="xs"
                        />
                        &nbsp;{services.translate("marketbuilder.importModal.downloadCSV.part1")}
                    </a>
                    &nbsp;{services.translate("marketbuilder.importModal.downloadCSV.part2")}
                </SectionBody>
            </>
        );
    }, [handleFiles, onDownloadSampleCsvFile, services, uploadErrorMsg]);

    const renderUploadSuccess = useCallback(() => {
        return (
            <>
                <StyledCSVFileSuccessImage />
                <UploadedFileBox>
                    <UploadedFileName>
                        <SWReactIcons
                            iconName="opportunities"
                            size="xs"
                        />
                        {uploadedFile.name}
                    </UploadedFileName>
                    <a
                        onClick={() => {
                            allTrackers.trackEvent(EVENT_CATEGORY, EVENT_ACTION, `By websites/importModal/clear`);
                            reset();
                        }}
                    >
                        {services.translate("marketbuilder.importModal.uploadedFile.clear")}
                    </a>
                </UploadedFileBox>
                <UploadedFileStats>
                    <SWReactIcons
                        iconName="checked"
                        size="xs"
                    />
                    {services.translate("marketbuilder.importModal.uploadedFile.stats.foundDomainsCount", {
                        count: importedDomains.length,
                    })}
                </UploadedFileStats>
            </>
        );
    }, [importedDomains, reset, services, uploadedFile]);

    const modalTitleText = useMemo(() => {
        if (uploadedFile) {
            return services.translate("marketbuilder.importModal.title.domainsSuccessfullyUploaded");
        }

        return modalTitle;
    }, [modalTitle, services, uploadedFile]);

    const UploadModalSection = useMemo(() => {
        if (uploadedFile) {
            return renderUploadSuccess();
        }

        return renderUploadFile();
    }, [renderUploadFile, renderUploadSuccess, uploadedFile]);

    return (
        <ProModal
            isOpen={isOpen}
            customStyles={proModalStyles}
            showCloseIcon={false}
            shouldCloseOnOverlayClick={true}
            onCloseClick={onClose}
        >
            <ModalTitle>{modalTitleText}</ModalTitle>
            {UploadModalSection}
            <ModalFooter>
                <CancelButton
                    onClick={() => {
                        allTrackers.trackEvent(EVENT_CATEGORY, EVENT_ACTION, `By websites/ImportModal/close`);
                        onClose();
                    }}
                >
                    {services.translate("marketbuilder.importModal.cancel")}
                </CancelButton>
                <ImportIconButton
                    iconName="download"
                    onClick={importDomains}
                    isDisabled={!uploadedFile || isImporting}
                    iconSize="xs"
                >
                    {services.translate("marketbuilder.importModal.importDomains")}
                </ImportIconButton>
            </ModalFooter>
        </ProModal>
    );
};
